import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Helmet } from "react-helmet-async";

function extractHeadings(md) {
  const lines = md.split(/\n/);
  const headings = [];
  lines.forEach((line) => {
    const m = line.match(/^(#{1,3})\s+(.*)$/);
    if (m) headings.push({ level: m[1].length, text: m[2] });
  });
  return headings;
}

const MarkdownView = ({ filePath }) => {
  const [content, setContent] = useState("");
  const tocRef = useRef(null);

  useEffect(() => {
    if (filePath) {
      fetch(filePath)
        .then((response) => response.text())
        .then((text) => setContent(text))
        .catch((error) => console.error("Error loading Markdown file:", error));
    }
  }, [filePath]);

  const headings = useMemo(() => extractHeadings(content), [content]);

  const components = useMemo(
    () => ({
      code({ inline, className, children, ...props }) {
        const raw = String(children).replace(/\n$/, "");
        if (inline) return <code className={className}>{raw}</code>;
        const onCopy = async () => {
          try {
            await navigator.clipboard.writeText(raw);
          } catch (e) {}
        };
        return (
          <div className="relative group">
            <button className="copy-btn" onClick={onCopy} aria-label="Copy">
              Copy
            </button>
            <pre className="code">
              <code className={className} {...props}>
                {raw}
              </code>
            </pre>
          </div>
        );
      },
      h1({ children }) {
        const text = String(children);
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return <h1 id={id}>{children}</h1>;
      },
      h2({ children }) {
        const text = String(children);
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return <h2 id={id}>{children}</h2>;
      },
      h3({ children }) {
        const text = String(children);
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        return <h3 id={id}>{children}</h3>;
      },
    }),
    []
  );

  const hasToc = headings.length > 0;
  return (
    <div className={hasToc ? "doc-layout" : "doc-content doc-single"}>
      <Helmet>
        <title>{`Guide - ${filePath || ""}`}</title>
        <meta
          name="description"
          content="Technical guide on Linux, DevOps and Security by Linux Sec Ops."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: filePath,
            author: { "@type": "Person", name: "Linux Sec Ops" },
          })}
        </script>
      </Helmet>
      {hasToc && (
        <aside className="toc" ref={tocRef}>
          <div className="toc-title">On this page</div>
          <ul>
            {headings.map((h, idx) => {
              const id = h.text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <li key={idx} className={`toc-l${h.level}`}>
                  <a href={`#${id}`}>{h.text}</a>
                </li>
              );
            })}
          </ul>
        </aside>
      )}
      <div className="doc-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default MarkdownView;
